/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Verdi Dashboard API
 * API behind the Verdi irrigation dashboard: organizations, their devices, zones and irrigation schedules, and the sensor data those devices report.
 *
 * ## Authentication
 *
 * Most operations need a Descope session token, sent as `Authorization: Bearer <token>`. Each operation's `security` lists what it accepts. Role and permission names there, such as `Verdi Administrator`, are Descope roles or permissions the session must carry, and the operation's description repeats them under **Access**. Operations with `security: []` are public.
 *
 * ## Organization context
 *
 * Data belongs to organizations. A user's session acts as their primary organization, or as another organization they belong to when its ID is sent in the `User` header. Administrators must send the `User` header on organization-scoped operations, or the request is rejected with a 400.
 *
 * ## Errors
 *
 * Error bodies are JSON `{ "message": string }`.
 *
 * - **400**: the path, query or body failed validation, or the request broke a domain rule.
 * - **401 / 403**: sent by the auth middleware; the body may be empty.
 * - **409**: see below.
 * - **500**: an unexpected server error.
 *
 * ## Versioned resources
 *
 * Versioned resources carry a `versionId`. Send the `versionId` you last read on a PATCH (in the body) or a DELETE (as a query parameter). If the resource changed since, the request gets a 409: reload it and retry. A 409 that includes a `code` is a conflict specific to that operation, described on the operation. On a create, 409 means the resource already exists.
 *
 * ## Reads with a request body
 *
 * Some reads take their input as a JSON body on the HTTP `QUERY` method; these operations are marked `x-read-only: true`. Deprecated POST and GET variants serve clients that cannot send `QUERY`.
 *
 * ## Identifiers
 *
 * MongoDB ObjectIds are 24-character hexadecimal strings.
 * OpenAPI spec version: 1.0.0
 */

import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "../custom-fetch.ts";
import type {
	ListSproutVerifications400,
	ListSproutVerifications500,
	ListSproutVerificationsParams,
	SproutVerificationInstance,
} from "./schemas";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === "queryKey") continue;
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

export const getListSproutVerificationsUrl = (
	params: ListSproutVerificationsParams,
) => {
	const normalizedParams = new URLSearchParams();

	Object.entries(params || {}).forEach(([key, value]) => {
		if (value !== undefined) {
			normalizedParams.append(key, value === null ? "null" : String(value));
		}
	});

	const stringifiedParams = normalizedParams.toString();

	return stringifiedParams.length > 0
		? `/sprout-verifications?${stringifiedParams}`
		: `/sprout-verifications`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary List sprout verifications for the authenticated user within a date range
 */
export const listSproutVerifications = async (
	params: ListSproutVerificationsParams,
	options?: Parameters<typeof customFetch>[1],
): Promise<SproutVerificationInstance[]> => {
	return customFetch<SproutVerificationInstance[]>(
		getListSproutVerificationsUrl(params),
		{
			...options,
			method: "GET",
		},
	);
};

export const getListSproutVerificationsQueryKey = (
	params?: ListSproutVerificationsParams,
) => {
	return ["sprout-verifications", ...(params ? [params] : [])] as const;
};

export const getListSproutVerificationsQueryOptions = <
	TData = Awaited<ReturnType<typeof listSproutVerifications>>,
	TError = ListSproutVerifications400 | void | ListSproutVerifications500,
>(
	params: ListSproutVerificationsParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listSproutVerifications>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey =
		queryOptions?.queryKey ?? getListSproutVerificationsQueryKey(params);

	const queryFn: QueryFunction<
		Awaited<ReturnType<typeof listSproutVerifications>>
	> = ({ signal }) =>
		listSproutVerifications(params, { signal, ...requestOptions });

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof listSproutVerifications>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSproutVerificationsQueryResult = NonNullable<
	Awaited<ReturnType<typeof listSproutVerifications>>
>;
export type ListSproutVerificationsQueryError =
	| ListSproutVerifications400
	| void
	| ListSproutVerifications500;

export function useListSproutVerifications<
	TData = Awaited<ReturnType<typeof listSproutVerifications>>,
	TError = ListSproutVerifications400 | void | ListSproutVerifications500,
>(
	params: ListSproutVerificationsParams,
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listSproutVerifications>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof listSproutVerifications>>,
					TError,
					Awaited<ReturnType<typeof listSproutVerifications>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSproutVerifications<
	TData = Awaited<ReturnType<typeof listSproutVerifications>>,
	TError = ListSproutVerifications400 | void | ListSproutVerifications500,
>(
	params: ListSproutVerificationsParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listSproutVerifications>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof listSproutVerifications>>,
					TError,
					Awaited<ReturnType<typeof listSproutVerifications>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSproutVerifications<
	TData = Awaited<ReturnType<typeof listSproutVerifications>>,
	TError = ListSproutVerifications400 | void | ListSproutVerifications500,
>(
	params: ListSproutVerificationsParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listSproutVerifications>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List sprout verifications for the authenticated user within a date range
 */

export function useListSproutVerifications<
	TData = Awaited<ReturnType<typeof listSproutVerifications>>,
	TError = ListSproutVerifications400 | void | ListSproutVerifications500,
>(
	params: ListSproutVerificationsParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listSproutVerifications>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getListSproutVerificationsQueryOptions(params, options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List sprout verifications for the authenticated user within a date range
 */
export const useSetListSproutVerificationsQueryData = () => {
	const queryClient = useQueryClient();
	return (
		params: ListSproutVerificationsParams | undefined,
		updater:
			| Awaited<ReturnType<typeof listSproutVerifications>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof listSproutVerifications>> | undefined,
			  ) => Awaited<ReturnType<typeof listSproutVerifications>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<
			Awaited<ReturnType<typeof listSproutVerifications>>
		>(
			{
				exact: $exactMatch,
				queryKey: getListSproutVerificationsQueryKey(params),
			},
			updater,
		);
	};
};

/**
 * @summary List sprout verifications for the authenticated user within a date range
 */
export const useGetListSproutVerificationsQueryData = () => {
	const queryClient = useQueryClient();
	return (params: ListSproutVerificationsParams) =>
		queryClient.getQueryData<
			Awaited<ReturnType<typeof listSproutVerifications>>
		>(getListSproutVerificationsQueryKey(params));
};
